import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core'
import {arrayMove, rectSortingStrategy, SortableContext} from '@dnd-kit/sortable'
import {CardBox} from '@hconnect/uikit/src/lib2'
import CallMergeIcon from '@mui/icons-material/CallMerge'
import CreateNewFolderOutlinedIcon from '@mui/icons-material/CreateNewFolderOutlined'
import LayersClearOutlinedIcon from '@mui/icons-material/LayersClearOutlined'
import {Alert, Box, BoxProps, Button, CircularProgress, Stack, Typography} from '@mui/material'
import Grid from '@mui/material/Unstable_Grid2'
import moment from 'moment'
import {useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'

import {ReactComponent as NoSelected} from '../../shared/assets/NoSelected.svg'
import {SignalSourceIcon} from '../../shared/components/SignalSourceIcon'
import {MultiScalableChart} from '../../shared/containers/charts/MultiScalableChart'
import {SortableChartWrapper} from '../../shared/containers/charts/SensorChartWrapper'
import {SingleScalableChart} from '../../shared/containers/charts/SingleScalableChart'
import {CombineModal} from '../../shared/containers/CombineModal'
import {useStoreUserValue} from '../../shared/hooks/userStorage/storeValue'
import {usePlantMonitoring, usePlantMonitoringSelectors} from '../context/PlantMonitoringContext'
import {useSignalMetadata} from '../context/SignalMetadataContext'
import {useGetGroupsData} from '../hooks/useGetGroupsData'
import {useSignalDescription} from '../hooks/useSignalDescription'

import {Feedback} from './Feedback'
import {AddGroupModalGraphs} from './SignalTree/Groups/AddGroupModalGraphs'

export const SignalCharts = ({
  disableGroupEdit,
  CardBoxProps
}: {
  disableGroupEdit: boolean
  CardBoxProps?: Omit<BoxProps, 'ref'>
}) => {
  const {t} = useTranslation()
  const {
    state: {chartLayout, activeGroup},
    actions
  } = usePlantMonitoring()
  const {selectedIds, getChartById, groupDataFromState} = usePlantMonitoringSelectors()
  const [addModalOpen, setAddModalOpen] = useState<boolean>(false)
  const [combineModalOpen, setCombineModalOpen] = useState<boolean>(false)
  const [editingCombinedChartId, setEditingCombinedChartId] = useState<string | null>()
  const {signals} = useSignalMetadata()
  const {mutateAsync: saveGroup} = useStoreUserValue()
  const {userGroups} = useGetGroupsData()

  const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor))

  const handleDragEnd = (event: DragEndEvent) => {
    const {active, over} = event
    if (!over || !active) return
    if (active.id !== over?.id) {
      const oldIndex = chartLayout.order.indexOf(active.id as string)
      const newIndex = chartLayout.order.indexOf(over.id as string)
      const newOrder = arrayMove(chartLayout.order, oldIndex, newIndex)
      actions.reorderCharts(newOrder)
    }
  }

  const groupsSettingsChanged = useMemo(() => {
    if (!activeGroup) return false
    const group = userGroups.find((g) => g.name === activeGroup)
    if (!group) return false
    if (groupDataFromState.columns !== group.columns) return true
    if (groupDataFromState.quickSelection !== group.quickSelection) return true
    if (JSON.stringify(groupDataFromState.chartsOrder) !== JSON.stringify(group.chartsOrder)) {
      // Charts order will catch any changes in combined charts as well as a sideffect
      return true
    }

    return false
  }, [activeGroup, groupDataFromState, userGroups])

  const updateGroupData = async () => {
    if (!activeGroup) return
    const updatedGroups = userGroups
      .filter((group) => group.type === 'user')
      .map((group) => {
        if (group.name === activeGroup) {
          return {
            ...group,
            columns: groupDataFromState.columns,
            quickSelection: groupDataFromState.quickSelection,
            chartsOrder: groupDataFromState.chartsOrder
          }
        }
        return group
      })
    await saveGroup({
      keyIdentifier: 'pm-user-groups',
      value: JSON.stringify(updatedGroups)
    })
  }

  const handleCombineViewClose = () => {
    setEditingCombinedChartId(null)
    setCombineModalOpen(false)
  }

  const handleCombineSignals = (combinedSignals: string[]) => {
    if (!editingCombinedChartId) {
      actions.combineSignals(combinedSignals)
    } else {
      actions.updateCombinedChart(editingCombinedChartId, combinedSignals)
    }
    handleCombineViewClose()
  }

  if (selectedIds.length === 0) {
    return (
      <Stack justifyContent="center" alignItems="center" gap={3} pt={10} pb={5}>
        <NoSelected />
        <Typography
          variant="body1"
          fontStyle="italic"
          color="rgba(255, 255, 255, 0.6)"
          maxWidth="300px"
        >
          {t('sensorDetails.noSelected')}
        </Typography>
      </Stack>
    )
  }

  return (
    <CardBox gap={2} display="flex" flexDirection={'column'} {...CardBoxProps}>
      <Box
        display="flex"
        justifyContent="space-between"
        flexDirection={{xs: 'column', sm: 'row'}}
        gap={2}
      >
        <Typography variant="h3">{`${selectedIds.length} ${t(
          'sensorDetails.signalsSelected'
        )}`}</Typography>
        <Stack direction="row" spacing={1.5}>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<LayersClearOutlinedIcon />}
            sx={{border: '1px solid #E8ECF0', borderRadius: '6px'}}
            onClick={actions.clearAllCharts}
          >
            {t('sensorDetails.clearAll')}
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<CallMergeIcon />}
            sx={{border: '1px solid #E8ECF0', borderRadius: '6px'}}
            onClick={() => setCombineModalOpen(true)}
          >
            {t('sensorDetails.compare')}
          </Button>
          {!activeGroup && !disableGroupEdit && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<CreateNewFolderOutlinedIcon />}
              sx={{borderRadius: '6px'}}
              onClick={() => setAddModalOpen(true)}
            >
              {t('groups.saveAsNew')}
            </Button>
          )}
          {groupsSettingsChanged && !disableGroupEdit && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<CreateNewFolderOutlinedIcon />}
              sx={{borderRadius: '6px'}}
              onClick={() => updateGroupData()}
            >
              {t('groups.updateSettings')}
            </Button>
          )}
          {/* Conditional render to unmount the component and reset internal state */}
          {addModalOpen && !disableGroupEdit && (
            <AddGroupModalGraphs isOpen={true} handleClose={() => setAddModalOpen(false)} />
          )}
        </Stack>
      </Box>
      <Feedback />
      <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
        <SortableContext items={chartLayout.order} strategy={rectSortingStrategy}>
          <Grid container spacing={2}>
            {chartLayout.order.map((id) => (
              <PlantMonitoringChart
                id={id}
                key={id}
                onEdit={() => {
                  setEditingCombinedChartId(id)
                  setCombineModalOpen(true)
                }}
              />
            ))}
          </Grid>
        </SortableContext>
      </DndContext>
      {combineModalOpen && (
        <CombineModal
          selectedSensors={signals?.filter((s) => selectedIds.includes(s.localName))}
          combinedSignals={getChartById(editingCombinedChartId ?? '')?.signalIds ?? []}
          onCancel={handleCombineViewClose}
          onDone={handleCombineSignals}
        />
      )}
    </CardBox>
  )
}

const PlantMonitoringChart = ({id, onEdit}: {id: string; onEdit: (chartId: string) => void}) => {
  const {actions} = usePlantMonitoring()
  const {getChartById} = usePlantMonitoringSelectors()

  const chartDefinition = getChartById(id)

  if (!chartDefinition) {
    return (
      <Alert severity="error" sx={{flexGrow: 1}}>
        Chart definition not found
      </Alert>
    )
  }

  return (
    <>
      {chartDefinition.type === 'single' ? (
        <SignalChart id={id} onRemove={actions.removeChart} />
      ) : (
        <MultiSignalChart
          chartId={id}
          ids={chartDefinition.signalIds}
          onRemove={() => actions.removeChart(id)}
          onEdit={() => onEdit(id)}
        />
      )}
    </>
  )
}

type ChartDateRange = {
  timeFrom: moment.Moment
  timeTo: moment.Moment
}

export const SignalChart = ({id, onRemove}: {id: string; onRemove: (id: string) => void}) => {
  const {state, actions} = usePlantMonitoring()
  const {start, end, zoomStart, zoomEnd} = state.timeRange
  const {effectiveColumns} = usePlantMonitoringSelectors()
  const {signalMap, isPending} = useSignalMetadata()
  const signal = signalMap.get(id)
  const getDescription = useSignalDescription()

  const range: ChartDateRange = useMemo(
    () => ({
      timeFrom: zoomStart ?? start,
      timeTo: zoomEnd ?? end
    }),
    [start, end, zoomStart, zoomEnd]
  )

  if (isPending) {
    return (
      <Grid xs={12 / effectiveColumns}>
        <Box
          sx={{height: '350px', display: 'flex', justifyContent: 'center', alignItems: 'center'}}
        >
          <CircularProgress />
        </Box>
      </Grid>
    )
  }

  if (!signal) {
    return (
      <Grid xs={12 / effectiveColumns}>
        <Box display="flex" flexGrow={1} justifyContent="center" alignItems="center">
          <Alert severity="error" sx={{flexGrow: 1}}>
            Signal not found
          </Alert>
        </Box>
      </Grid>
    )
  }

  return (
    <Grid xs={12 / effectiveColumns}>
      <SortableChartWrapper
        id={id}
        subtitle={getDescription({description: signal.description, name: signal.name})}
        title={
          <Box display="flex" alignItems="center" gap={1}>
            <SignalSourceIcon source={signal.source ?? ''} />
            <Typography noWrap>{signal.localName}</Typography>
          </Box>
        }
        onRemove={() => onRemove(id)}
      >
        <SingleScalableChart
          sensorId={id}
          range={range}
          rangeSelectCallback={(value) => actions.setZoomRange(value.timeFrom, value.timeTo)}
          scaleToShowThreshold={state.scaleToThresholds}
        />
      </SortableChartWrapper>
    </Grid>
  )
}

export const MultiSignalChart = ({
  chartId,
  ids,
  onRemove,
  onEdit
}: {
  chartId: string
  ids: string[]
  onRemove: () => void
  onEdit: () => void
}) => {
  const {t} = useTranslation()
  const {state, actions} = usePlantMonitoring()
  const {start, end, zoomStart, zoomEnd} = state.timeRange
  const {effectiveColumns} = usePlantMonitoringSelectors()
  const {signals, isPending} = useSignalMetadata()
  const selected = signals?.filter((s) => ids.includes(s.localName))

  const range: ChartDateRange = useMemo(
    () => ({
      timeFrom: zoomStart ?? start,
      timeTo: zoomEnd ?? end
    }),
    [start, end, zoomStart, zoomEnd]
  )

  if (isPending) {
    return (
      <Grid xs={12 / effectiveColumns}>
        <Box
          sx={{height: '350px', display: 'flex', justifyContent: 'center', alignItems: 'center'}}
        >
          <CircularProgress />
        </Box>
      </Grid>
    )
  }

  if (!selected) {
    return (
      <Grid xs={12 / effectiveColumns}>
        <Box display="flex" flexGrow={1} justifyContent="center" alignItems="center">
          <Alert severity="error" sx={{flexGrow: 1}}>
            Signal not found
          </Alert>
        </Box>
      </Grid>
    )
  }

  return (
    <Grid xs={12 / effectiveColumns}>
      <SortableChartWrapper
        id={chartId}
        title={`${t('combine.chartTitle')} (${ids.join(', ')})`}
        onRemove={() => onRemove()}
        onEdit={() => onEdit()}
      >
        <MultiScalableChart
          sensorData={signals.map((s) => ({...s, unit: s.unit.name, svgId: ''}))}
          sensorIds={ids}
          range={range}
          rangeSelectCallback={(value) => actions.setZoomRange(value.timeFrom, value.timeTo)}
          scaleToShowThreshold={state.scaleToThresholds}
        />
      </SortableChartWrapper>
    </Grid>
  )
}
